/**
 * Recent Listings Storage
 *
 * Tracks recently viewed listings for quick access
 */

export interface RecentListing {
  id: string;
  title: string;
  city?: string;
  state?: string;
  price: number;
  image?: string;
  timestamp: number;
}

const STORAGE_KEY = 'usland-recent-listings';
const MAX_RECENT_LISTINGS = 10;

/**
 * Get recent listings from localStorage
 */
export function getRecentListings(): RecentListing[] {
  if (typeof window === 'undefined') return [];

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error reading recent listings:', error);
  }

  return [];
}

/**
 * Add a listing to recent listings
 */
export function addRecentListing(listing: RecentListing): void {
  if (typeof window === 'undefined') return;

  const recent = getRecentListings();

  // Remove if already exists (to move to top)
  const filtered = recent.filter(item => item.id !== listing.id);

  // Add new item at the beginning
  const updated = [{ ...listing, timestamp: Date.now() }, ...filtered];

  // Keep only MAX_RECENT_LISTINGS
  const limited = updated.slice(0, MAX_RECENT_LISTINGS);

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(limited));
  } catch (error) {
    console.error('Error saving recent listing:', error);
  }
}

/**
 * Remove a specific listing from recent listings
 */
export function removeRecentListing(id: string): void {
  if (typeof window === 'undefined') return;

  const recent = getRecentListings();
  const filtered = recent.filter(item => item.id !== id);

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error('Error removing recent listing:', error);
  }
}

/**
 * Clear all recent listings
 */
export function clearRecentListings(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing recent listings:', error);
  }
}
